import React from 'react';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
	ServiceSaveImpact,
	formatDelta,
	formatMoney,
} from 'pages/SalesOrder/SalesOrderScreen/helpers/serviceSaveImpact';

interface ServiceSaveConfirmDialogProps {
	open: boolean;
	impact: ServiceSaveImpact | null;
	serviceLabel: string;
	onCancel: () => void;
	onConfirm: () => void;
	confirming?: boolean;
}

const DeltaCell = ({
	value,
	currency,
	invertPositive,
}: {
	value: number;
	currency: string;
	invertPositive?: boolean;
}): JSX.Element => {
	if (value === 0) {
		return (
			<TableCell align="right">
				<Typography variant="body2" color="text.secondary">
					—
				</Typography>
			</TableCell>
		);
	}

	const isNegative = invertPositive ? value > 0 : value < 0;
	const color = isNegative ? 'error.main' : value > 0 ? 'success.main' : 'text.primary';

	return (
		<TableCell align="right">
			<Typography variant="body2" sx={{ color, fontWeight: value !== 0 ? 600 : 400 }}>
				{formatDelta(value, currency)}
			</Typography>
		</TableCell>
	);
};

export const ServiceSaveConfirmDialog = ({
	open,
	impact,
	serviceLabel,
	onCancel,
	onConfirm,
	confirming = false,
}: ServiceSaveConfirmDialogProps): JSX.Element => {
	if (!impact) {
		return <></>;
	}

	const { service, saleOrder } = impact;
	const currency = saleOrder.currency;
	const serviceMarginNegative = service.after.margin < 0;
	const soMarginWorsened = saleOrder.deltaMargin < 0;

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
			<DialogTitle>Confirmar cambios en el servicio</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					{serviceLabel}
				</Typography>

				{(serviceMarginNegative || soMarginWorsened) && (
					<Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
						{serviceMarginNegative && 'El margen del servicio quedará negativo. '}
						{soMarginWorsened && 'El margen total de la orden de venta disminuirá.'}
					</Alert>
				)}

				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Cambios en el servicio
				</Typography>
				<Table size="small" sx={{ mb: 3 }}>
					<TableHead>
						<TableRow>
							<TableCell />
							<TableCell align="right">Antes</TableCell>
							<TableCell align="right">Después</TableCell>
							<TableCell align="right">Δ</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>Costo total</TableCell>
							<TableCell align="right">{formatMoney(service.before.totalCost, currency)}</TableCell>
							<TableCell align="right">{formatMoney(service.after.totalCost, currency)}</TableCell>
							<DeltaCell value={service.deltaCost} currency={currency} invertPositive />
						</TableRow>
						<TableRow>
							<TableCell>Venta total</TableCell>
							<TableCell align="right">{formatMoney(service.before.totalSaleAmount, currency)}</TableCell>
							<TableCell align="right">{formatMoney(service.after.totalSaleAmount, currency)}</TableCell>
							<DeltaCell value={service.deltaSale} currency={currency} />
						</TableRow>
						<TableRow>
							<TableCell>Margen del servicio</TableCell>
							<TableCell align="right">{formatMoney(service.before.margin, currency)}</TableCell>
							<TableCell align="right">{formatMoney(service.after.margin, currency)}</TableCell>
							<DeltaCell value={service.deltaMargin} currency={currency} />
						</TableRow>
					</TableBody>
				</Table>

				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Impacto en la orden de venta
				</Typography>
				<Box
					sx={{
						p: 2,
						borderRadius: 1,
						border: '1px solid',
						borderColor: 'divider',
						backgroundColor: 'action.hover',
					}}
				>
					<Typography variant="body2">
						Margen total SO:{' '}
						<strong>
							{formatMoney(saleOrder.before.grossProfit, currency)} →{' '}
							{formatMoney(saleOrder.after.grossProfit, currency)}
						</strong>
						{saleOrder.deltaMargin !== 0 && (
							<Typography
								component="span"
								variant="body2"
								sx={{ ml: 1, color: saleOrder.deltaMargin < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}
							>
								({formatDelta(saleOrder.deltaMargin, currency)})
							</Typography>
						)}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Venta total SO: {formatMoney(saleOrder.before.saleAmount, currency)}
						{service.deltaSale !== 0 && <> → {formatMoney(saleOrder.after.saleAmount, currency)}</>}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onCancel} disabled={confirming}>
					Cancelar
				</Button>
				<Button onClick={onConfirm} variant="contained" color="primary" disabled={confirming}>
					Confirmar y guardar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
